<script>
    /**
     * First-visit language hint. Shown once the i18n core decided (from navigator.languages) that the
     * visitor prefers one of our non-English languages. Written in THAT language, with an English line,
     * anchored under the header's globe icon when the header is present (top-right otherwise).
     * Closing / "keep English" is remembered; choosing a language is remembered through setLocale().
     */
    import { onMount, onDestroy } from 'svelte';
    import { fly } from 'svelte/transition';
    import { suggestion, acceptSuggestion, dismissSuggestion, translate, localeInfo } from './index.js';

    let visible = false;
    let card = null;
    let top = 16, right = 16, caretRight = null;
    let timer = null;

    $: code = $suggestion;
    $: info = code ? localeInfo(code) : null;
    $: tt = key => (code ? translate(code, key) : '');
    $: englishLine = code ? translate('en', 'suggest.available_in', { language: translate('en', 'language.names.' + code) }) : '';

    function place() {
        if (typeof document === 'undefined') return;
        const anchor = document.getElementById('i18n-switcher');
        const nav = anchor && anchor.closest('.header-navbar');
        if (anchor && nav) {
            const a = anchor.getBoundingClientRect();
            const n = nav.getBoundingClientRect();
            top = Math.round(n.bottom + 8);
            right = 12;
            const width = card ? card.offsetWidth : 380;
            const centerFromRight = window.innerWidth - (a.left + a.width / 2);
            caretRight = Math.max(16, Math.min(width - 24, Math.round(centerFromRight - right - 7)));
        } else {
            top = 16; right = 16; caretRight = null;
        }
    }

    $: if (code) {
        clearTimeout(timer);
        timer = setTimeout(() => { visible = true; setTimeout(place, 0); }, 500);
    } else {
        visible = false;
    }

    onMount(() => {
        window.addEventListener('resize', place);
        return () => window.removeEventListener('resize', place);
    });
    onDestroy(() => clearTimeout(timer));

    function onKey(e) { if (visible && e.key === 'Escape') dismissSuggestion(); }
</script>

<svelte:window on:keydown={onKey} />

<style>
    .i18n-suggest {
        position: fixed; z-index: 1045;
        width: 400px; max-width: calc(100vw - 24px);
        background: #FFFFFF; color: #475F7B;
        border: 1px solid #DFE3E7; border-left: 4px solid #5A8DEE; border-radius: 0.5rem;
        box-shadow: 0 10px 30px rgba(25, 42, 70, 0.25);
        padding: 1rem 1.1rem 1rem 1rem;
        font-size: 0.95rem; line-height: 1.5;
    }
    .i18n-suggest-caret {
        position: absolute; top: -8px; width: 14px; height: 14px;
        background: #FFFFFF; border-left: 1px solid #DFE3E7; border-top: 1px solid #DFE3E7;
        transform: rotate(45deg);
    }
    .i18n-suggest-close {
        position: absolute; top: 6px; right: 8px;
        border: 0; background: transparent; color: #828D99; font-size: 1.4rem; line-height: 1; cursor: pointer; padding: 0.2rem 0.4rem;
    }
    .i18n-suggest-close:hover { color: #475F7B; }
    .i18n-suggest-body { display: flex; gap: 0.8rem; align-items: flex-start; }
    .i18n-suggest-icon { flex: 0 0 auto; width: 2.4rem; height: 2.4rem; border-radius: 50%; background: #E2ECFF; color: #5A8DEE; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; }
    .i18n-suggest-title { font-size: 1.05rem; font-weight: 600; margin: 0.15rem 1.4rem 0.35rem 0; color: #475F7B; }
    .i18n-suggest-text { margin: 0 0 0.35rem 0; }
    .i18n-suggest-en { margin: 0 0 0.8rem 0; font-size: 0.82rem; color: #828D99; }
    .i18n-suggest-actions { display: flex; flex-wrap: wrap; gap: 0.5rem; }
    .i18n-suggest-actions .btn { font-size: 0.9rem; }
    @media (max-width: 575.98px) {
        .i18n-suggest { left: 12px; right: 12px !important; width: auto; }
    }
</style>

{#if visible && info}
    <div class="i18n-suggest" role="dialog" aria-live="polite" aria-labelledby="i18n-suggest-title" lang={code}
         style="top:{top}px; right:{right}px;" bind:this={card} in:fly={{ y: -12, duration: 250 }}>
        {#if caretRight !== null}<span class="i18n-suggest-caret" style="right:{caretRight}px;"></span>{/if}
        <button type="button" class="i18n-suggest-close" aria-label={tt('common.close')} title={tt('common.close')} on:click={() => dismissSuggestion()}>&times;</button>
        <div class="i18n-suggest-body">
            <div class="i18n-suggest-icon"><i class="bx bx-globe"></i></div>
            <div>
                <h5 id="i18n-suggest-title" class="i18n-suggest-title">{tt('suggest.title')}</h5>
                <p class="i18n-suggest-text">{tt('suggest.body')}</p>
                <p class="i18n-suggest-en" lang="en">{englishLine}</p>
                <div class="i18n-suggest-actions">
                    <button type="button" class="btn btn-primary btn-sm" on:click={acceptSuggestion}>{tt('suggest.accept')}</button>
                    <button type="button" class="btn btn-light btn-sm" on:click={() => dismissSuggestion()}>{tt('suggest.decline')}</button>
                </div>
            </div>
        </div>
    </div>
{/if}
